import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box } from '@heycater/design-system';
import { ArrowForwardIos } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import FlexBox from 'shared/components/FlexBox';
import { useWindowSize } from 'shared/hooks/useWindowSize';

const ReadMoreArea = ({ children, maxHeight = 350 }) => {
  const [isExpanded, setExpanded] = useState(false);
  const [contentHeight, setContentHeight] = useState();
  const contentWrapperRef = useRef(null);
  const { t } = useTranslation('common');
  const windowSize = useWindowSize();

  const maxNonExpandableHeight = maxHeight - 50;

  const isExpandable = contentHeight > maxHeight;

  useEffect(() => {
    setContentHeight(contentWrapperRef?.current?.scrollHeight);
  }, [isExpanded, windowSize]);

  useLayoutEffect(() => {
    setExpanded(false);
  }, [isExpandable]);

  const wrapperHeight = isExpanded
    ? contentHeight
    : Math.min(contentHeight, maxNonExpandableHeight);

  return (
    <>
      <Wrapper
        ref={contentWrapperRef}
        isExpanded={isExpanded}
        maxHeight={wrapperHeight}
      >
        {children}
        <WhiteFade hidden={isExpanded || !isExpandable} />
      </Wrapper>
      {isExpandable && (
        <FlexBox justifyContent="center">
          <ReadMoreButton onClick={() => setExpanded(!isExpanded)}>
            {isExpanded ? t('action.collapse') : t('action.readMore')}
            <Box fontSize={18} ml={1} mt={1}>
              <ArrowIcon
                fontSize="inherit"
                direction={isExpanded ? 'up' : 'down'}
              />
            </Box>
          </ReadMoreButton>
        </FlexBox>
      )}
    </>
  );
};

const Wrapper = styled.div`
  position: relative;
  overflow: hidden;
  transition: max-height 0.3s ease;
  max-height: ${({ maxHeight, isExpanded }) =>
    isExpanded ? maxHeight : maxHeight + 10}px;
`;

const ArrowIcon = styled(ArrowForwardIos)`
  transform: rotate(${({ direction }) => (direction === 'down' ? 90 : -90)}deg);
`;

const ReadMoreButton = styled.button`
  font-weight: 600;
  display: flex;
  align-items: center;
  font-size: 14px;
  line-height: 172.5%;
  border: none;
  background-color: inherit;

  text-align: center;
  text-decoration-line: underline;

  :hover {
    color: rgba(0, 0, 0, 0.75);
  }

  cursor: pointer;
`;

const WhiteFade = styled.div`
  height: 90px;
  width: 100%;
  left: 0;
  right: 0;
  position: absolute;
  bottom: 0;
  z-index: 10;
  opacity: ${({ hidden }) => (hidden ? 0 : 1)};
  transition: all, 0.3s ease;
  background: linear-gradient(
    0deg,
    #fff 0%,
    #fff 10%,
    rgba(255, 255, 255, 0) 100%
  );

  transform: ${({ position }) =>
    position === 'left' ? 'rotate(-180deg)' : 'none'};
`;

export default ReadMoreArea;
